import React, { useEffect, useState } from 'react';
import { Tabs, Tab, Box, Backdrop, CircularProgress, Typography, Divider, List, Card, ListItem, ListItemText } from "@mui/material";
import UserReportChart from "../dashboard/components/UserReportChart";
import {getAllSectionProgress, getSectionProgressByUser, getUserProgressBySection, getAllSections} from "../../services/onboardingService/onboardingService";
import usersService from "../../services/usersService";
import { TextField, Autocomplete } from "@mui/material";
import AlertSnackbar from "../../components/AlertSnackbar";
import { set } from 'react-hook-form';
import { DataGrid } from "@mui/x-data-grid";
import moment from "moment";

const OnboardingInsightsPage = () => {
    const [tabIndex, setTabIndex] = useState(0);
    const [chartData, setChartData] = useState([]);
    const [userList, setUserList] = useState([]);
    const [sectionList, setSectionList] = useState([]);
    const [sectionSpecificData, setSectionSpecificData] = useState([]);
    const [userSpecificData, setUserSpecificData] = useState([]);
    const [isLoading, setIsLoading]= useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" }); 
    const [selectedSection, setSelectedSection] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    
    const handleTabChange = (event, newIndex) => {
        setTabIndex(newIndex);
    };

    const fetchChartData = async () => {
        try {
            setIsLoading(true);
            const response = await getAllSectionProgress();
            const responseData = response.data.data;
            const chartData = responseData.map((section) => {
                return {
                    date: [...section.title.split(" ").slice(0, 3)],
                    value: section.completedCount,
                    tooltipX: section.title,
                }
            }
            );
            setChartData(chartData);
        } catch (error) {
            console.error("Error fetching chart data: ", error);
            setSnackbar({
                open: true,
                message: error.response.data.message || "Something Went Wrong",
                severity: "error",
              })
        } finally {
            setIsLoading(false);
        }
    }

    const fetchUsersInfo = async () => {
        try {
            setIsLoading(true);
            const resp = await usersService.getAllUserDetails({status: 'active', orderBy: 'name'});
            const activeUsers = resp?.data?.data?.users?.filter(user=> user.facebookUserName) 
            setUserList(activeUsers || []);
        } catch (error) {
            console.log(error);
            setSnackbar({
                open: true,
                message: error.response.data.message || "Something Went Wrong",
                severity: "error",
              })
        } finally {
            setIsLoading(false);
        }
    };


    const fetchSectionsInfo = async () => {
        try{
            setIsLoading(true);
            const resp = await getAllSections();
            setSectionList(resp?.data?.data || [])
        }
        catch(error){
            console.log(error);
            setSnackbar({
                open: true,
                message: error.response.data.message || "Something Went Wrong",
                severity: "error",
              })
        } finally {
            setIsLoading(false);
        }
    }

    const fetchSectionProgress = async (section) => {
        try{
            console.log(section);
            if(!section) {
                setSectionSpecificData([]);
                return;
            }
            setIsLoading(true);
            const resp = await getUserProgressBySection(section.sectionId);
            const sectionProgress = resp?.data?.data || [];
            setSectionSpecificData(sectionProgress);
        }
        catch(error){
            console.log(error);
            setSnackbar({
                open: true,
                message: error.response.data.message || "Something Went Wrong",
                severity: "error",
              })
        } finally {
            setIsLoading(false);
        }
    }

    const fetchUserProgress = async (user) => {
        try{
            if(!user) {
                setUserSpecificData([]);
                return;
            }
            setIsLoading(true);
            const resp = await getSectionProgressByUser(user.userId, user.facebookUserId);
            const userProgress = resp?.data?.data || [];
            setUserSpecificData(userProgress);  
        }
        catch(error){
            console.log(error);
            setSnackbar({
                open: true,
                message: error.response.data.message || "Something Went Wrong",
                severity: "error",
              })
        } finally {
            setIsLoading(false);
        }
    }

    const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") {
          return;
        }
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    useEffect(() => {
        fetchChartData();
    }, []);

    useEffect(() => {
        if(tabIndex === 1 && userList.length === 0){
            fetchUsersInfo();
        } else if(tabIndex === 0 && sectionList.length === 0){
            fetchSectionsInfo();
        }
    }, [tabIndex]);

    return (
        <>
            <Box>
                <Typography variant="h1" gutterBottom>
                    Onboarding Insights
                </Typography>
                <Divider sx={{ marginBottom: "1em" }} />
                <UserReportChart data={chartData} title={"Number of users completed"} />
                <Tabs value={tabIndex} onChange={handleTabChange} aria-label="onboarding insights tabs">
                    <Tab label="View Insights By Section" />
                    <Tab label="View Insights By User" />
                </Tabs>
                {tabIndex === 0 && (
                    <Box p={3}>
                        <Autocomplete
                            options={sectionList}
                            getOptionLabel={(option) => option.title}
                            renderInput={(params) => <TextField {...params} label="Select Section" variant="outlined" />}
                            value={selectedSection}
                            onChange={(event, value) => {
                                setSelectedSection(value);
                                fetchSectionProgress(value);
                            }}
                        />
                        {selectedSection && 
                            <Box sx={{ height: 400, width: '100%', mt: 2 }}>
                                <DataGrid
                                    rows={sectionSpecificData.map((data, index) => ({
                                        id: index,
                                        firstName: data.user?.firstName || "-",
                                        lastName: data.user?.lastName || "-",
                                        email: data.user?.email || "-",
                                        completionDate: data.updatedAt|| "-",
                                    }))}
                                    columns={[
                                        { field: 'firstName', headerName: 'First Name', flex: 1 },
                                        { field: 'lastName', headerName: 'Last Name', flex: 1 },
                                        { field: 'email', headerName: 'Email', flex: 1 },
                                        { field: 'completionDate', headerName: 'Completion Date', flex: 2,
                                            renderCell: (params) => {
                                                return moment(params.value).format("lll");
                                            },
                                            valueGetter: (value) => {
                                                return moment(value).format("lll");
                                            } 
                                        },
                                    ]}
                                    initialState={{
                                        pagination: {
                                            paginationModel: {
                                            pageSize: 5,
                                            },
                                        },
                                    }}
                                    pageSizeOptions={[5, 10, 20]}
                                />
                            </Box>
                        }   
                    </Box>
                )}
                {tabIndex === 1 && (
                    <Box p={3}>
                        <Autocomplete
                            options={userList}
                            getOptionLabel={(option) => `${option.firstName} ${option.lastName} - ${option.email}`}
                            renderInput={(params) => <TextField {...params} label="Select User" variant="outlined" />}
                            value={selectedUser}
                            onChange={(event, value) => {
                                fetchUserProgress(value);
                                setSelectedUser(value);
                            }}
                        />
                        {selectedUser &&
                            <Box sx={{ height: 400, width: '100%', mt: 2 }}>
                                <DataGrid
                                    rows={userSpecificData.map((data, index) => ({
                                        id: index,
                                        title: data.home_section.title || "-",
                                        completionDate: data.updatedAt|| "-",
                                    }))}
                                    columns={[
                                        { field: 'title', headerName: 'Title', flex: 1 },
                                        { field: 'completionDate', headerName: 'Completion Date', flex: 2,
                                            renderCell: (params) => {
                                                return moment(params.value).format("lll");
                                            },
                                            valueGetter: (value) => {
                                                return moment(value).format("lll");
                                            } 
                                        },
                                    ]}
                                    initialState={{
                                        pagination: {
                                            paginationModel: {
                                            pageSize: 5,
                                            },
                                        },
                                    }}
                                    pageSizeOptions={[5, 10, 20]}
                                />
                            </Box>
                        }
                    </Box>
                )}
            </Box>
            {isLoading && 
                <Backdrop
                    sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={isLoading}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
            }
            <AlertSnackbar
                open={snackbar.open}
                handleClose={handleCloseSnackbar}
                message={snackbar.message}
                severity={snackbar.severity}
            />
        </>
    );
};


export default OnboardingInsightsPage;